import React from "react";
import { Box } from "@mui/material";
import SectionFrame from "../SectionFrame";
import { EditableButton, EditableText } from "../InlineEditable";
import EditableImage from "../EditableImage";

export default function AboutSection({
  form,
  active,
  device,
  onSelect,
  onCommit,
  onUploadImage,
  onRemoveImage,
  disabled,
}) {
  const isMobile = device === "mobile";
  const sectionAppearance = form.appearance?.about || {};
  const surfaceColor = sectionAppearance.surfaceColor || "transparent";
  const hasSurfaceColor = Boolean(sectionAppearance.surfaceColor);
  const primary =
    sectionAppearance.accentColor || form.theme.primaryColor || "#F35A3A";
  const secondary =
    sectionAppearance.titleColor || form.theme.secondaryColor || "#19242F";
  const textColor =
    sectionAppearance.textColor || form.theme.mutedTextColor || "#5B5D73";
  const backgroundColor =
    sectionAppearance.backgroundColor ||
    form.theme.backgroundColor ||
    "rgba(25,36,47,.035)";
  const imageOnRight = form.about.layout === "image-right";

  return (
    <SectionFrame
      id="about"
      label="Sobre el negocio"
      active={active}
      enabled={form.sections.showAbout !== false}
      onSelect={onSelect}
      onToggle={(next) => onCommit("sections.showAbout", next)}
    >
      <Box
        sx={{
          px: isMobile ? 2.5 : 5,
          pr: isMobile ? 2.5 : 15,
          py: isMobile ? "var(--wp-space-48)" : "var(--wp-space-64)",
          display: "grid",
          gridTemplateColumns: isMobile
            ? "1fr"
            : "minmax(0,.9fr) minmax(0,1.1fr)",
          gap: 4,
          bgcolor: backgroundColor,
        }}
      >
        <Box sx={{ order: isMobile ? 1 : imageOnRight ? 2 : 1 }}>
          <EditableImage
            value={form.about.imageUrl}
            label="Imagen del negocio"
            height={isMobile ? 240 : 340}
            disabled={disabled}
            onUpload={(file) =>
              onUploadImage("about.imageUrl", file, {
                sectionKey: "about",
                assetKey: "image",
              })
            }
            onRemove={() => onRemoveImage("about.imageUrl")}
            sx={{
              backgroundPosition: form.about.imagePosition || "center",
              transform: "scale(var(--wp-segment-image-scale))",
              transformOrigin: "center",
            }}
          />
        </Box>

        <Box
          sx={{
            alignSelf: "center",
            order: isMobile ? 2 : imageOnRight ? 1 : 2,
            bgcolor: surfaceColor,
            borderRadius: hasSurfaceColor ? 3 : 0,
            p: hasSurfaceColor ? 2.5 : 0,
          }}
        >
          <EditableText
            value={form.about.eyebrow}
            fallback="Sobre nuestro negocio"
            disabled={disabled}
            onSave={(value) => onCommit("about.eyebrow", value)}
            sx={{
              color: primary,
              fontWeight: 950,
              textTransform: "uppercase",
              letterSpacing: 1.2,
              fontSize: 13,
            }}
          />
          <Box sx={{ mt: 0.6 }}>
            <EditableText
              value={form.about.title}
              fallback={`Conocé más sobre ${form.business.name || "el negocio"}`}
              disabled={disabled}
              multiline
              onSave={(value) => onCommit("about.title", value)}
              sx={{
                color: secondary,
                fontWeight: "var(--wp-segment-title-weight)",
                fontSize: isMobile ? "var(--wp-title-31)" : "var(--wp-title-42)",
                letterSpacing: "var(--wp-segment-letter-spacing)",
                lineHeight: 1.08,
              }}
            />
          </Box>
          <Box sx={{ mt: 1.8 }}>
            <EditableText
              value={form.about.description}
              fallback="Contá la historia del negocio, su experiencia, su forma de trabajar y el valor que entrega a cada cliente."
              disabled={disabled}
              multiline
              minRows={4}
              onSave={(value) => onCommit("about.description", value)}
              sx={{ color: textColor, lineHeight: 1.75 }}
            />
          </Box>
          <Box sx={{ mt: 2.2 }}>
            <EditableButton
              value={form.about.ctaLabel}
              fallback="Contactanos"
              disabled={disabled}
              onSave={(value) => onCommit("about.ctaLabel", value)}
              sx={{
                color: primary,
                bgcolor: "transparent",
                px: 0,
                py: 0.5,
                fontWeight: 950,
              }}
            />
          </Box>
        </Box>
      </Box>
    </SectionFrame>
  );
}
